---
title: "Choosie — the in-app AI assistant"
description: "Choosie is the AI assistant inside Chosen. Ask it questions in plain language and it searches and acts — with every change held for your approval first."
category: "hq"
keywords:
  - "Choosie AI assistant"
  - "AI recruiting assistant"
  - "ATS chat assistant"
  - "AI ATS automation"
schema: "HowTo"
nav:
  section: "choosie"
  order: 10
---

Choosie is the AI assistant built into Chosen. It's a chat box: you type what you want in plain language, and Choosie answers. It can search your candidates, pull up a job, and — with your sign-off — change things. Questions get answered on the spot. Anything that *changes* your data waits for you to approve it first.

That split is the whole idea. Choosie reaches into Chosen through 40-plus tools, so it can do a lot. But a read and a write are not treated the same. A read runs immediately. A write is written up as a plan and paused until you say go. You get an assistant that can act, without one that acts behind your back.

## In this section

<Steps>
  <li>[Asking Choosie questions](/choosie/asking-questions) — the no-risk half: searching candidates, pulling up a record, asking about your pipeline. Reads happen the moment you ask.</li>
  <li>[Plans and approvals](/choosie/plans-and-approvals) — how a request to change something becomes a plan you review, approve, or reject before anything happens.</li>
  <li>[What Choosie can do](/choosie/what-it-can-do) — a grouped map of its capabilities across candidates, jobs, stages, feedback, and scheduling — and an honest list of what it can't.</li>
  <li>[Charts and tables in Choosie](/choosie/charts-and-tables) — analytics questions come back as charts and tables, rendered in chat, with PNG and CSV export.</li>
  <li>[Writing good prompts](/choosie/good-prompts) — specific beats vague. How to phrase a request so Choosie gets it right the first time.</li>
</Steps>

<Callout type="tip" title="Choosie acts as you, with your role">
Choosie does everything under your account and your permissions. If your [role](/getting-started/accounts-and-roles) can't delete a job, Choosie can't delete one for you. It's also scoped to your organization — it never sees another company's data.
</Callout>
